import React from "react";
import { UseFormRegister, FieldErrors } from "react-hook-form";
import { Plane } from "lucide-react";
import { FormField, Input } from "../UI/FormField";
import { SectionCard } from "../UI/SectionCard";
import { CreateShipmentData } from "../../types";
import { useTranslation } from "react-i18next";

interface AirDetailsSectionProps {
  register: UseFormRegister<CreateShipmentData>;
  errors: FieldErrors<CreateShipmentData>;
  readonly: boolean;
  getErrorMessage: (error: any) => string | undefined;
}

// Airline (carrier) details — required when transportMode = AIR.
// Same structure as SEA: the forwarder sub-block is always optional
// (they manage the MAWB and issue their own HAWB).
export const AirDetailsSection: React.FC<AirDetailsSectionProps> = ({
  register,
  errors,
  readonly,
  getErrorMessage,
}) => {
  const { t } = useTranslation("shipments");
  const airErrors = errors.airDetails;
  const forwarderErrors = airErrors?.forwarder;

  return (
    <SectionCard icon={Plane} title={t("air.title")}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-1">
        <div className="space-y-1">
          <FormField
            layout="inline"
            size="sm"
            label={t("air.airlineName")}
            error={getErrorMessage(airErrors?.airlineName)}
          >
            <Input
              {...register("airDetails.airlineName")}
              error={!!airErrors?.airlineName}
              placeholder={t("air.airlineNamePlaceholder")}
              readOnly={readonly}
              size="sm"
            />
          </FormField>

          <FormField
            layout="inline"
            size="sm"
            label={t("air.flightNumber")}
            error={getErrorMessage(airErrors?.flightNumber)}
          >
            <Input
              {...register("airDetails.flightNumber")}
              error={!!airErrors?.flightNumber}
              placeholder={t("air.flightNumberPlaceholder")}
              readOnly={readonly}
              size="sm"
            />
          </FormField>

          <FormField
            layout="inline"
            size="sm"
            label={t("air.airportOfDeparture")}
            error={getErrorMessage(airErrors?.airportOfDeparture)}
          >
            <Input
              {...register("airDetails.airportOfDeparture")}
              error={!!airErrors?.airportOfDeparture}
              placeholder={t("air.airportOfDeparturePlaceholder")}
              readOnly={readonly}
              size="sm"
            />
          </FormField>

          <FormField
            layout="inline"
            size="sm"
            label={t("air.airportOfArrival")}
            error={getErrorMessage(airErrors?.airportOfArrival)}
          >
            <Input
              {...register("airDetails.airportOfArrival")}
              error={!!airErrors?.airportOfArrival}
              placeholder={t("air.airportOfArrivalPlaceholder")}
              readOnly={readonly}
              size="sm"
            />
          </FormField>
        </div>

        <div className="space-y-1">
          <FormField
            layout="inline"
            size="sm"
            label={t("air.mawbNumber")}
            error={getErrorMessage(airErrors?.mawbNumber)}
          >
            <Input
              {...register("airDetails.mawbNumber")}
              error={!!airErrors?.mawbNumber}
              placeholder={t("air.mawbNumberPlaceholder")}
              readOnly={readonly}
              size="sm"
            />
          </FormField>

          <FormField
            layout="inline"
            size="sm"
            label={t("air.mawbDate")}
            error={getErrorMessage(airErrors?.mawbDate)}
          >
            <Input
              {...register("airDetails.mawbDate")}
              type="date"
              error={!!airErrors?.mawbDate}
              readOnly={readonly}
              size="sm"
            />
          </FormField>

          <FormField
            layout="inline"
            size="sm"
            label={t("air.uldNumber")}
            error={getErrorMessage(airErrors?.uldNumber)}
          >
            <Input
              {...register("airDetails.uldNumber")}
              error={!!airErrors?.uldNumber}
              placeholder={t("air.uldNumberPlaceholder")}
              readOnly={readonly}
              size="sm"
            />
          </FormField>

          <FormField
            layout="inline"
            size="sm"
            label={t("air.etd")}
            error={getErrorMessage(airErrors?.etd)}
          >
            <Input
              {...register("airDetails.etd")}
              type="date"
              error={!!airErrors?.etd}
              readOnly={readonly}
              size="sm"
            />
          </FormField>

          <FormField
            layout="inline"
            size="sm"
            label={t("air.eta")}
            error={getErrorMessage(airErrors?.eta)}
          >
            <Input
              {...register("airDetails.eta")}
              type="date"
              error={!!airErrors?.eta}
              readOnly={readonly}
              size="sm"
            />
          </FormField>
        </div>
      </div>

      {/* Forwarder — optional */}
      <div className="mt-3 pt-3 border-t border-gray-100">
        <h4 className="text-[12px] font-semibold text-gray-500 uppercase tracking-wide mb-2">
          {t("forwarder.title")}
        </h4>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-1">
          <div className="space-y-1">
            <FormField
              layout="inline"
              size="sm"
              label={t("forwarder.companyName")}
              error={getErrorMessage(forwarderErrors?.companyName)}
            >
              <Input
                {...register("airDetails.forwarder.companyName")}
                error={!!forwarderErrors?.companyName}
                placeholder={t("forwarder.companyNamePlaceholder")}
                readOnly={readonly}
                size="sm"
              />
            </FormField>

            <FormField
              layout="inline"
              size="sm"
              label={t("forwarder.contactPerson")}
              error={getErrorMessage(forwarderErrors?.contactPerson)}
            >
              <Input
                {...register("airDetails.forwarder.contactPerson")}
                error={!!forwarderErrors?.contactPerson}
                placeholder={t("forwarder.contactPersonPlaceholder")}
                readOnly={readonly}
                size="sm"
              />
            </FormField>

            <FormField
              layout="inline"
              size="sm"
              label={t("labels.email")}
              error={getErrorMessage(forwarderErrors?.email)}
            >
              <Input
                {...register("airDetails.forwarder.email")}
                type="email"
                error={!!forwarderErrors?.email}
                placeholder={t("placeholders.email")}
                readOnly={readonly}
                size="sm"
              />
            </FormField>
          </div>

          <div className="space-y-1">
            <FormField
              layout="inline"
              size="sm"
              label={t("labels.phone")}
              error={getErrorMessage(forwarderErrors?.phone)}
            >
              <Input
                {...register("airDetails.forwarder.phone")}
                error={!!forwarderErrors?.phone}
                placeholder={t("placeholders.phone")}
                readOnly={readonly}
                size="sm"
              />
            </FormField>

            <FormField
              layout="inline"
              size="sm"
              label={t("forwarder.hawbNumber")}
              error={getErrorMessage(forwarderErrors?.hawbNumber)}
            >
              <Input
                {...register("airDetails.forwarder.hawbNumber")}
                error={!!forwarderErrors?.hawbNumber}
                placeholder={t("forwarder.hawbNumberPlaceholder")}
                readOnly={readonly}
                size="sm"
              />
            </FormField>

            <FormField
              layout="inline"
              size="sm"
              label={t("forwarder.reference")}
              error={getErrorMessage(forwarderErrors?.reference)}
            >
              <Input
                {...register("airDetails.forwarder.reference")}
                error={!!forwarderErrors?.reference}
                placeholder={t("forwarder.referencePlaceholder")}
                readOnly={readonly}
                size="sm"
              />
            </FormField>
          </div>
        </div>
      </div>
    </SectionCard>
  );
};

AirDetailsSection.displayName = "AirDetailsSection";
